import { ref, set, onValue, get, remove } from 'firebase/database';
import { realtimeDb } from '@/lib/firebase';
import { logger } from '@/lib/logger';

// 16-color palette for demo canvas
export const DEMO_CANVAS_COLORS = [
  '#FF0000', // Red
  '#00FF00', // Green
  '#0000FF', // Blue
  '#FFFF00', // Yellow
  '#FF00FF', // Magenta
  '#00FFFF', // Cyan
  '#FFA500', // Orange
  '#800080', // Purple
  '#FFC0CB', // Pink
  '#A52A2A', // Brown
  '#808080', // Gray
  '#000000', // Black
  '#FFFFFF', // White
  '#008000', // Dark Green
  '#000080', // Navy
  '#FF1493', // Deep Pink
];

const SESSION_ID_KEY = 'demo_canvas_session_id';
const USER_COLOR_KEY_PREFIX = 'demo_canvas_user_color_';
const CANVAS_SIZE = 18; // 504 / 18 = 28
const MAX_PIXELS_PER_USER = 25;

/**
 * Generate or retrieve guest session ID
 */
export const getOrCreateSessionId = (): string => {
  if (typeof window === 'undefined') {
    // Server-side: generate a temporary ID
    return `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  let sessionId = localStorage.getItem(SESSION_ID_KEY);
  
  if (!sessionId) {
    // Generate a unique session ID
    sessionId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  
  return sessionId;
};

/**
 * Get user identifier (userId if authenticated, otherwise sessionId)
 */
export const getUserIdentifier = (userId: string | null): string => {
  if (userId) {
    return userId;
  }
  return getOrCreateSessionId();
};

/**
 * Hash a string to a number (for consistent color assignment)
 */
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
};

/**
 * Get or assign a color for a user/guest
 * Colors are consistently assigned based on identifier
 */
export const getUserColor = (identifier: string): string => {
  if (typeof window === 'undefined') {
    // Server-side: hash to color
    const hash = hashString(identifier);
    return DEMO_CANVAS_COLORS[hash % DEMO_CANVAS_COLORS.length];
  }

  const storageKey = `${USER_COLOR_KEY_PREFIX}${identifier}`;
  let color = localStorage.getItem(storageKey);
  
  if (!color) {
    // Assign color based on hash of identifier
    const hash = hashString(identifier);
    color = DEMO_CANVAS_COLORS[hash % DEMO_CANVAS_COLORS.length];
    localStorage.setItem(storageKey, color);
  }
  
  return color;
};

/**
 * Get all pixels belonging to a user
 */
const getUserPixels = async (identifier: string): Promise<Array<{ x: number; y: number; pixel: DemoPixel }>> => {
  const canvasRef = ref(realtimeDb, 'demo_canvas/pixels');
  const snapshot = await get(canvasRef);
  const data = snapshot.val();
  
  const userPixels: Array<{ x: number; y: number; pixel: DemoPixel }> = [];
  
  if (data) {
    for (let y = 0; y < CANVAS_SIZE; y++) {
      if (data[y]) {
        for (let x = 0; x < CANVAS_SIZE; x++) {
          if (data[y][x] && data[y][x].userId === identifier) {
            userPixels.push({ x, y, pixel: data[y][x] as DemoPixel });
          }
        }
      }
    }
  }
  
  return userPixels;
};

/**
 * Place a pixel on the demo canvas
 * If user has 25 or more pixels, removes the oldest one first
 */
export const placeDemoPixel = async (
  x: number,
  y: number,
  color: string,
  identifier: string
): Promise<void> => {
  try {
    // Validate coordinates
    if (x < 0 || x >= CANVAS_SIZE || y < 0 || y >= CANVAS_SIZE) {
      throw new Error('Invalid coordinates');
    }

    // Validate color
    if (!DEMO_CANVAS_COLORS.includes(color)) {
      throw new Error('Invalid color');
    }

    // Get all pixels belonging to this user
    const userPixels = await getUserPixels(identifier);
    
    // If user has reached the limit, remove the oldest pixel
    if (userPixels.length >= MAX_PIXELS_PER_USER) {
      // Find the oldest pixel (lowest timestamp)
      let oldestPixel = userPixels[0];
      for (const pixelData of userPixels) {
        if (pixelData.pixel.timestamp < oldestPixel.pixel.timestamp) {
          oldestPixel = pixelData;
        }
      }
      
      // Remove the oldest pixel
      const oldestPixelRef = ref(realtimeDb, `demo_canvas/pixels/${oldestPixel.y}/${oldestPixel.x}`);
      await remove(oldestPixelRef);
    }

    // Place the new pixel
    const pixelRef = ref(realtimeDb, `demo_canvas/pixels/${y}/${x}`);
    await set(pixelRef, {
      color,
      userId: identifier,
      timestamp: Date.now(),
    });
  } catch (error) {
    logger('demoCanvas').error('Error placing demo pixel:', error);
    throw error;
  }
};

/**
 * Pixel data structure
 */
export interface DemoPixel {
  color: string;
  userId: string;
  timestamp: number;
}

/**
 * Canvas state structure
 */
export type DemoCanvasState = (DemoPixel | null)[][];

/**
 * Subscribe to demo canvas changes
 * Returns unsubscribe function
 */
export const subscribeToDemoCanvas = (
  callback: (canvas: DemoCanvasState) => void
): (() => void) => {
  const canvasRef = ref(realtimeDb, 'demo_canvas/pixels');
  
  const unsubscribe = onValue(canvasRef, (snapshot) => {
    try {
      const data = snapshot.val();
      
      // Initialize empty canvas
      const canvas: DemoCanvasState = [];
      for (let y = 0; y < CANVAS_SIZE; y++) {
        canvas[y] = Array(CANVAS_SIZE).fill(null);
      }
      
      // Fill canvas with pixel data
      if (data) {
        for (let y = 0; y < CANVAS_SIZE; y++) {
          if (data[y]) {
            for (let x = 0; x < CANVAS_SIZE; x++) {
              if (data[y][x]) {
                canvas[y][x] = data[y][x] as DemoPixel;
              }
            }
          }
        }
      }
      
      callback(canvas);
    } catch (error) {
      logger('demoCanvas').error('Error processing canvas snapshot:', error);
      // Return empty canvas on error
      const emptyCanvas: DemoCanvasState = [];
      for (let y = 0; y < CANVAS_SIZE; y++) {
        emptyCanvas[y] = Array(CANVAS_SIZE).fill(null);
      }
      callback(emptyCanvas);
    }
  }, (error) => {
    logger('demoCanvas').error('Error subscribing to demo canvas:', error);
    // Return empty canvas on error
    const emptyCanvas: DemoCanvasState = [];
    for (let y = 0; y < CANVAS_SIZE; y++) {
      emptyCanvas[y] = Array(CANVAS_SIZE).fill(null);
    }
    callback(emptyCanvas);
  });
  
  return unsubscribe;
};

/**
 * Get canvas size
 */
export const getCanvasSize = (): number => {
  return CANVAS_SIZE;
};

